import { combineReducers, configureStore } from '@reduxjs/toolkit'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { persistReducer, PersistConfig } from 'redux-persist'
import { getPersistConfig } from 'redux-deep-persist'
import { accountSlice, ACCOUNT_KEY } from '../lib/account/account.slice'
import { BETTING_KEY, bettingSlice, BettingCartState } from '../lib/betting/betting.slice'
import { consumerApi } from '../lib/consumer-api/consumer-api'
import { fantasyInSeasonApi } from '../lib/fantasy-in-season-api/fantasy-in-season-api'
import { fantasySlice, FANTASY_FILTER_KEY } from '../lib/fantasy/fantasy.slice'
import { SubscriptionsSlice, SUB_KEY } from '../lib/subscription-plans/subscription-plans.slice'
import { webViewSlice, WEB_VIEW_KEY } from '../lib/web-view/web-view.slice'
import { FEED_KEY, FeedState, feedReducer } from '../lib/feed/feed.slice'
import { bottomDrawerSlice, BOTTOM_DRAWER_KEY } from '../lib/bottom-drawer/bottom-drawer.slice'
import { APP_STATE_KEY, appSlice } from '../lib/app/app.slice'
import { connectSlice, CONNECT_KEY, ConnectState } from '../lib/connect/connect.slice'
import { PLAYER_PROFILE_KEY, playerProfileSlice } from '../lib/player-profile/player-profile.slice'
import { merlinApi } from '../lib/merlin-api/merlin-api'
import { geoServicesApi } from '../lib/geo-services/geo-services'
import { apiLogger } from './utils/apiLoggerMiddleware'
import { listenerMiddleware } from './middlewares/common-listener-middleware'

declare const process: any

const persistBettingConfig: PersistConfig<BettingCartState> = {
  key: 'root',
  storage: AsyncStorage,
  whitelist: ['betsCart', 'betsCartLength', 'sportsbookBetsCartLength', 'showBetsTrackerView', 'enableGamesLockout'],
}

const persistFeedConfig: PersistConfig<FeedState> = {
  key: 'root',
  storage: AsyncStorage,
  whitelist: ['feedBookmark.articleIds', 'feedBookmark.unreadArticles'],
}

const persistConnectConfig: PersistConfig<ConnectState> = {
  key: 'root',
  storage: AsyncStorage,
  whitelist: ['visitedEventsTimestamp'],
}

const reducers = combineReducers({
  // Add the generated reducer as a specific top-level slice
  [consumerApi.reducerPath]: consumerApi.reducer,
  [fantasyInSeasonApi.reducerPath]: fantasyInSeasonApi.reducer,
  [FANTASY_FILTER_KEY]: fantasySlice.reducer,
  [WEB_VIEW_KEY]: webViewSlice.reducer,
  [BETTING_KEY]: persistReducer(persistBettingConfig, bettingSlice.reducer),
  [SUB_KEY]: SubscriptionsSlice.reducer,
  [ACCOUNT_KEY]: accountSlice.reducer,
  [FEED_KEY]: persistReducer(getPersistConfig({ ...persistFeedConfig, rootReducer: feedReducer }), feedReducer),
  [BOTTOM_DRAWER_KEY]: bottomDrawerSlice.reducer,
  [APP_STATE_KEY]: appSlice.reducer,
  [CONNECT_KEY]: persistReducer(persistConnectConfig, connectSlice.reducer),
  [PLAYER_PROFILE_KEY]: playerProfileSlice.reducer,
  [merlinApi.reducerPath]: merlinApi.reducer,
  [geoServicesApi.reducerPath]: geoServicesApi.reducer,
})

const persistedReducer = reducers
const devTools = process.env.NODE_ENV === 'development'

const middlewares = [
  consumerApi.middleware,
  fantasyInSeasonApi.middleware,
  merlinApi.middleware,
  geoServicesApi.middleware,
]

// __DEV__ is only available in RN development, don't add on web because we don't need it
if (typeof __DEV__ !== 'undefined' && __DEV__) {
  middlewares.push(apiLogger)
}

export const makeStore = () =>
  configureStore({
    reducer: persistedReducer,
    devTools,
    // Adding the api middleware enables caching, invalidation, polling,
    // and other useful features of `rtk-query`.
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
      })
        .prepend(listenerMiddleware.middleware)
        .concat(middlewares),
  })

export const makeTestStore = (preloadedState?: any) =>
  configureStore({
    reducer: persistedReducer,
    devTools,
    preloadedState,
    // Adding the api middleware enables caching, invalidation, polling,
    // and other useful features of `rtk-query`.
    middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(middlewares),
  })
